import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { engagementPaths, scopeFactors } from '../data/pricing';
import { pageMeta, useDocumentMeta } from '../hooks/useDocumentMeta';
export function PricingPlan() {
  useDocumentMeta(pageMeta.pricing);
  return <>
      <section className="relative overflow-hidden bg-ink pb-12 pt-12 lg:pb-14 lg:pt-16" data-id="element-415" data-mp="1r046gztkf170">
        <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-416" data-mp="2eq8ctey51f1p" />
        <div className="absolute -left-32 top-0 h-[30rem] w-[30rem] rounded-full bg-forge-orange/[0.09] blur-[130px]" aria-hidden="true" data-id="element-417" data-mp="2jni1gsdpdmoh" />

        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-418" data-mp="24xkegfrif30v">
          <p className="font-mono text-[11.5px] uppercase tracking-[0.18em] text-forge-cyan" data-id="element-419" data-mp="29x5hb19svviv">Project scope &amp; pricing</p>
          <h1 className="mt-3 max-w-3xl font-display text-[2rem] font-extrabold leading-[1.08] tracking-[-0.02em] text-white sm:text-[2.5rem]" data-id="element-420" data-mp="2xmhmlq20bjfw">
            Every project is scoped before it is priced.
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-mist-200" data-id="element-421" data-mp="17l25p7vwtw9c">
            We first understand the requirements, integrations, timeline, and ongoing support needs. You then receive a
            written scope and price before work begins.
          </p>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy-900 py-12 lg:py-16" data-id="element-422" data-mp="1vad1sc2bf2h5">
        <div className="absolute inset-0 bp opacity-50" aria-hidden="true" data-id="element-423" data-mp="1vcg15mpcy7yj" />
        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-424" data-mp="1qba6vkg1ucmw">
          <p className="font-mono text-[11.5px] uppercase tracking-[0.16em] text-mist-400" data-id="element-425" data-mp="257req1p1594p">Common engagement paths</p>

          <div className="mt-5 grid gap-5 lg:grid-cols-3" data-id="element-426" data-mp="20a99ala81c1g">
            {engagementPaths.map((path, i) => <motion.article key={path.name} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: i * 0.07
          }} className="flex min-w-0 flex-col rounded-2xl border border-white/10 bg-navy-950/70 p-7" data-id="element-427" data-mp="av1ktjynvzxb" data-mpi={i}>
                <h2 className="font-display text-[1.25rem] font-extrabold leading-snug text-white" data-id="element-428" data-mp="14jzdvmwpg7d6">{path.name}</h2>
                <p className="mt-3 text-[13.5px] leading-relaxed text-mist-200" data-id="element-429" data-mp="19jverda3vioi">{path.summary}</p>

                <p className="mt-6 border-t border-white/10 pt-5 font-mono text-[11.5px] uppercase tracking-[0.14em] text-mist-400" data-id="element-430" data-mp="1ejhrzmpxoyxb">
                  Typical work
                </p>
                <ul className="mt-3 space-y-2.5" data-id="element-431" data-mp="1nai5oj9mk4a1">
                  {path.typicalWork.map(item => <li key={item} className="flex min-w-0 gap-2.5 text-[13.5px] leading-snug text-mist-100" data-id="element-432" data-mp="1sampoj9qrh50">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rotate-45 bg-forge-orange" aria-hidden="true" data-id="element-433" data-mp="1xa90km23klan" />
                      {item}
                    </li>)}
                </ul>
              </motion.article>)}
          </div>

          <div className="mt-6" data-id="element-434" data-mp="3hri7i6uubit1">
            <Link to="/#get-started" className="group inline-flex items-center gap-2 rounded-xl bg-forge-orange px-6 py-3.5 text-[15px] font-bold text-ink transition-transform hover:-translate-y-0.5" data-id="element-435" data-mp="3hq71e0dx9yyr">
              Discuss Your Project
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-436" data-mp="9byglehn6ch5" />
            </Link>
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-7 lg:px-9" data-id="element-437" data-mp="4d3xebfrilwi">
            <p className="font-mono text-[11.5px] uppercase tracking-[0.16em] text-forge-cyan" data-id="element-438" data-mp="3vowyvh4nm0nn">What affects project scope</p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-439" data-mp="s1mwq1gu3ocg">
              {scopeFactors.map(factor => <li key={factor} className="flex min-w-0 gap-2.5 text-[14px] leading-snug text-mist-100" data-id="element-440" data-mp="nay42itf4lbq">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rotate-45 bg-forge-cyan" aria-hidden="true" data-id="element-441" data-mp="7s596a34ce4v" />
                  {factor}
                </li>)}
            </ul>
            <p className="mt-6 border-t border-white/10 pt-5 text-[13.5px] leading-relaxed text-mist-300" data-id="element-442" data-mp="3u16k0ocx0z7b">
              Every project is covered by our published 30-day money-back guarantee. Hosting, SSL, and monitoring are included
              on the systems WebsiteForge builds and maintains.
            </p>
          </div>
        </div>
      </section>
    </>;
}